import { CheckCircle, Clock, Truck, Package, MapPin, Phone, Download, Star, ChevronRight } from "lucide-react";

// Static demo page to visualize the client portal
const SuiviDemo = () => {
  const statut = "en_cours"; // Change to "livree" or "en_attente" to preview different states

  const steps = [
    { key: "en_attente", label: "Commande confirmée", icon: Package, done: true },
    { key: "en_cours",   label: "En cours de livraison", icon: Truck, done: statut === "en_cours" || statut === "livree" },
    { key: "livree",     label: "Livré", icon: CheckCircle, done: statut === "livree" },
  ];

  const currentStep = steps.findIndex(s => s.key === statut);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex flex-col items-center justify-start py-8 px-4">

      {/* Header */}
      <div className="w-full max-w-md mb-6 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 mb-3">
          <img src="https://i.imgur.com/placeholder.png" alt="logo" className="w-10 h-10 hidden" />
          <span className="text-white font-bold text-lg">TC</span>
        </div>
        <h1 className="text-white font-bold text-xl tracking-tight">TopCocoon</h1>
        <p className="text-indigo-300 text-sm mt-0.5">Suivi de votre livraison</p>
      </div>

      {/* Main Card */}
      <div className="w-full max-w-md space-y-4">

        {/* Status Hero */}
        <div className={`rounded-2xl p-5 border ${
          statut === "livree"
            ? "bg-green-500/10 border-green-400/30"
            : statut === "en_cours"
            ? "bg-indigo-500/10 border-indigo-400/30"
            : "bg-slate-500/10 border-slate-400/30"
        } backdrop-blur-sm`}>
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              statut === "livree" ? "bg-green-500" : statut === "en_cours" ? "bg-indigo-500" : "bg-slate-500"
            }`}>
              {statut === "livree" ? <CheckCircle className="w-7 h-7 text-white" /> :
               statut === "en_cours" ? <Truck className="w-7 h-7 text-white" /> :
               <Clock className="w-7 h-7 text-white" />}
            </div>
            <div>
              <p className="text-white/60 text-xs uppercase tracking-widest font-medium">Statut actuel</p>
              <p className={`font-bold text-xl mt-0.5 ${
                statut === "livree" ? "text-green-400" : statut === "en_cours" ? "text-indigo-300" : "text-slate-300"
              }`}>
                {statut === "livree" ? "Livré ✓" : statut === "en_cours" ? "En route..." : "En attente"}
              </p>
              {statut === "en_cours" && (
                <p className="text-white/50 text-sm mt-0.5">Livraison prévue aujourd'hui</p>
              )}
            </div>
          </div>
        </div>

        {/* Progress Timeline */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5">
          <div className="space-y-0">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              const isActive = idx === currentStep;
              const isDone = step.done;
              return (
                <div key={step.key} className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                      isDone
                        ? "bg-indigo-500 border-indigo-400"
                        : isActive
                        ? "bg-indigo-500/30 border-indigo-400 animate-pulse"
                        : "bg-white/5 border-white/20"
                    }`}>
                      <Icon className={`w-4 h-4 ${isDone || isActive ? "text-white" : "text-white/30"}`} />
                    </div>
                    {idx < steps.length - 1 && (
                      <div className={`w-0.5 h-8 mt-1 ${isDone ? "bg-indigo-400/60" : "bg-white/10"}`} />
                    )}
                  </div>
                  <div className="pt-1.5 pb-6">
                    <p className={`font-medium text-sm ${isDone || isActive ? "text-white" : "text-white/30"}`}>
                      {step.label}
                    </p>
                    {isActive && statut === "en_cours" && (
                      <p className="text-indigo-300 text-xs mt-0.5">Le livreur est en chemin</p>
                    )}
                    {step.key === "livree" && statut === "livree" && (
                      <p className="text-green-400 text-xs mt-0.5">Aujourd'hui à 14h32</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Order Details */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 space-y-4">
          <h3 className="text-white/70 text-xs uppercase tracking-widest font-medium">Détails de votre commande</h3>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
              <Package className="w-5 h-5 text-indigo-300" />
            </div>
            <div>
              <p className="text-white font-semibold">2x Chaise Milan Noir</p>
              <p className="text-white/50 text-sm">Réf. MIL-BLK</p>
            </div>
            <span className="ml-auto text-white font-bold">70 €</span>
          </div>

          <div className="h-px bg-white/10" />

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-500/20 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-white/50 text-xs uppercase tracking-wide mb-0.5">Adresse de livraison</p>
              <p className="text-white text-sm font-medium">Rue de la Loi 16</p>
              <p className="text-white/60 text-sm">1000 Bruxelles</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-500/20 flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-white/50 text-xs uppercase tracking-wide mb-0.5">Client</p>
              <p className="text-white text-sm font-medium">Karima Mouradi</p>
            </div>
          </div>
        </div>

        {/* Receipt (visible when livree) */}
        {statut === "livree" && (
          <button className="w-full flex items-center justify-between p-4 bg-green-500/10 border border-green-400/30 rounded-2xl backdrop-blur-sm group hover:bg-green-500/20 transition-all">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
                <Download className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-semibold text-sm">Télécharger mon reçu</p>
                <p className="text-green-400 text-xs">PDF • Reçu de livraison</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-green-400 group-hover:translate-x-1 transition-transform" />
          </button>
        )}

        {/* Facebook CTA */}
        <a
          href="https://www.facebook.com/TopCocoon/reviews?locale=fr_FR"
          target="_blank"
          rel="noreferrer"
          className="w-full flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-sm hover:bg-white/10 transition-all group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <Star className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <p className="text-white font-semibold text-sm">Laisser un avis</p>
              <p className="text-white/50 text-xs">Votre avis compte beaucoup pour nous</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-white/30 group-hover:translate-x-1 transition-transform" />
        </a>

        {/* Footer */}
        <p className="text-center text-white/25 text-xs pb-4">
          TopCocoon • Livraison de mobilier en Belgique
        </p>
      </div>
    </div>
  );
};

export default SuiviDemo;
